/**
 * Slack Settings card. Bot token goes to workspace_secrets (SLACK_BOT_TOKEN).
 * Channel id is workspace config, not a secret. The token is never shown again after save.
 */
import { useCallback, useEffect, useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SettingsSection } from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

interface SlackIntegrationSectionProps {
  organizationId?: string | null
  canManage?: boolean
}

function orgQs(organizationId?: string | null): string {
  if (!organizationId) return ''
  return `?organizationId=${encodeURIComponent(organizationId)}`
}

export function SlackIntegrationSection({
  organizationId,
  canManage = false,
}: SlackIntegrationSectionProps) {
  const [hasToken, setHasToken] = useState(false)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState('')
  const [channelId, setChannelId] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/slack/status${orgQs(organizationId)}`)
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not load Slack status')
        return
      }
      setHasToken(Boolean(data.has_token))
      setChannelId(typeof data.channel_id === 'string' ? data.channel_id : '')
    } catch {
      setError('Could not load Slack status')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void load()
  }, [load])

  async function saveSettings() {
    if (!organizationId || !channelId.trim()) return
    if (!token.trim() && !hasToken) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/slack/settings${orgQs(organizationId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          channel_id: channelId.trim(),
          ...(token.trim() ? { token: token.trim() } : {}),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Could not save Slack settings')
        return
      }
      setToken('')
      setShowToken(false)
      setHasToken(true)
      setChannelId(typeof data.channel_id === 'string' ? data.channel_id : channelId.trim())
      setMessage('Saved. Slack accepted the bot and the channel.')
    } catch {
      setError('Could not save Slack settings')
    } finally {
      setSaving(false)
    }
  }

  async function testConnection() {
    if (!organizationId) return
    setTesting(true)
    setError(null)
    setMessage(null)
    try {
      const res = await fetchWithAuth(`/api/slack/test${orgQs(organizationId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : 'Connection test failed')
        return
      }
      const team = typeof data.team === 'string' && data.team ? ` Team: ${data.team}.` : ''
      setMessage(`Connected.${team} Sample message count: ${data.sample_count ?? 0}.`)
    } catch {
      setError('Connection test failed')
    } finally {
      setTesting(false)
    }
  }

  const canSave = Boolean(channelId.trim()) && (Boolean(token.trim()) || hasToken)

  return (
    <SettingsSection
      title="Bot token and channel"
      description="Bot token from a Slack app installed on this workspace. One public channel id (starts with C). Agents can read that channel and its threads. They cannot post or react."
      data-testid={testIds.workspaceSettings.slackSection}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {hasToken ? 'A bot token is saved.' : 'No bot token saved yet.'}
            {channelId ? ` Channel ${channelId}.` : ' No channel id yet.'}
          </p>
          {canManage ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="slack-channel-id">Channel id</Label>
                <Input
                  id="slack-channel-id"
                  value={channelId}
                  autoComplete="off"
                  placeholder="C0123456789"
                  onChange={(e) => setChannelId(e.target.value)}
                  className="max-w-xl"
                  data-testid={testIds.workspaceSettings.slackChannelId}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slack-bot-token">Bot token</Label>
                <div className="relative max-w-xl">
                  <Input
                    id="slack-bot-token"
                    type={showToken ? 'text' : 'password'}
                    value={token}
                    autoComplete="off"
                    placeholder={hasToken ? 'Paste a new token to replace' : 'Paste bot token'}
                    onChange={(e) => setToken(e.target.value)}
                    data-testid={testIds.workspaceSettings.slackBotToken}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowToken((v) => !v)}
                    aria-label={showToken ? 'Hide token' : 'Show token'}
                  >
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => void saveSettings()}
                  disabled={saving || !canSave}
                  data-testid={testIds.workspaceSettings.slackSaveSettings}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void testConnection()}
                  disabled={testing || !hasToken || !channelId.trim()}
                  data-testid={testIds.workspaceSettings.slackTestConnection}
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test connection'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">An admin saves the bot token and channel.</p>
          )}
          {message ? (
            <p className="text-sm" data-testid={testIds.workspaceSettings.slackStatusMessage}>
              {message}
            </p>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive" data-testid={testIds.workspaceSettings.slackStatusMessage}>
              {error}
            </p>
          ) : null}
        </div>
      )}
    </SettingsSection>
  )
}
